// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import Head from 'next/head';
import { cloudinaryUrl } from '@realadvisor/cloudinary';
import { format } from 'date-fns';
import { useRouter } from '../../controls/router';
import { getPlaceSubTitle, getPlaceTitle } from '../translate/place';
import { translatePropertyMainType } from '../translate/propertyTypes';
import { useLocale } from '../../hooks/locale';
import { useUserData } from '../../hooks/user-data.js';
import type { AggregatesListingsPageMeta_aggregatesListings$key } from './__generated__/AggregatesListingsPageMeta_aggregatesListings.graphql';
import type { AggregatesListingsPageMeta_root$key } from './__generated__/AggregatesListingsPageMeta_root.graphql';

type Props = {|
  aggregatesListings: AggregatesListingsPageMeta_aggregatesListings$key,
  root: AggregatesListingsPageMeta_root$key,
  placeData: any,
|};

export const AggregatesListingsPageMeta = (props: Props): React.Node => {
  const { t, interpolate, dateLocale } = useLocale();
  const router = useRouter();
  const { origin } = useUserData();

  const root = useFragment(
    graphql`
      fragment AggregatesListingsPageMeta_root on Query {
        cms {
          pageWithData(uid: $uid, lang: $lang) {
            page {
              description
            }
          }
        }
      }
    `,
    props.root,
  );
  const aggregatesListings = useFragment(
    graphql`
      fragment AggregatesListingsPageMeta_aggregatesListings on AggregatesListingConnectionConnection {
        totalCount
        edges {
          node {
            firstImage: images(first: 1) {
              bucketName
              fileName
            }
          }
        }
      }
    `,
    props.aggregatesListings,
  );

  const page = root.cms.pageWithData?.page;

  const title = getPlaceTitle(t, props.placeData, 'plural');
  let titleWithCount = null;
  const translatePropertyMainTypes = translatePropertyMainType(
    t,
    props.placeData.propertyType ?? '',
  );

  // Better meta title with count when there are more than 3 listings
  if (
    aggregatesListings.totalCount > 3 &&
    props.placeData.offerType === 'buy'
  ) {
    titleWithCount = t('propertiesForSaleInLocationWithCount', {
      ...translatePropertyMainTypes,
      count: aggregatesListings.totalCount,
      month: format(new Date(), 'MMMM', { locale: dateLocale }),
      year: format(new Date(), 'yyyy', { locale: dateLocale }),
      inLocation: props.placeData.inLabel,
    });
  } else if (
    aggregatesListings.totalCount > 3 &&
    props.placeData.offerType === 'rent'
  ) {
    titleWithCount = t('propertiesForRentInLocationWithCount', {
      ...translatePropertyMainTypes,
      count: aggregatesListings.totalCount,
      month: format(new Date(), 'MMMM', { locale: dateLocale }),
      year: format(new Date(), 'yyyy', { locale: dateLocale }),
      inLocation: props.placeData.inLabel,
    });
  }

  const description = interpolate(
    page?.description ?? getPlaceSubTitle(t, props.placeData),
    {
      totalCount: aggregatesListings.totalCount,
      topTitle: title,
      bottomTitle: getPlaceSubTitle(t, props.placeData),
      inLabel: props.placeData.inLabel,
      label: props.placeData.label,
      ...translatePropertyMainTypes,
    },
  );

  const firstImage = aggregatesListings.edges?.[0]?.node.firstImage[0];

  const firstImageUrl =
    firstImage &&
    cloudinaryUrl(
      { f: 'jpg' },
      { bucketName: firstImage.bucketName, fileName: firstImage.fileName },
    );

  const path = router.asPath.split('?')[0];

  return (
    <Head>
      <title>{titleWithCount ?? title}</title>
      <meta name="description" content={description} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={firstImageUrl} />
      <meta name="twitter:site" content="RealAdvisor" />
      <meta name="twitter:url" content={`${origin}${path}`} />
      <meta property="og:title" content={title} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${origin}${path}`} />
      <meta property="og:site_name" content="RealAdvisor" />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={firstImageUrl} />
      <meta name="apple-itunes-app" content="app-id=6504883593" />
    </Head>
  );
};
